(() => {
"use strict";
if (window.__ardmoreProductCardsLoaded) return;
window.__ardmoreProductCardsLoaded = true;
/*
 * Quick Add to Cart from Collection Pages
 * Rebuilds each collection grid card with a per-card image carousel and a quick-add control on the price row.
 * Products without options add straight to cart. Products with options open option pills or a dropdown in the card
 * on desktop and a bottom sheet on mobile. Quote-only products add to quote through the RFQ app.
 */

const testId = "ARD-005";

// Scopes the stylesheet (.ARD-005_var_1).
document.documentElement.classList.add(`${testId}_var_1`);

const isMobile = window.matchMedia("(max-width: 749px)");

const SELECTORS = {
	grid: "#product-grid",
	item: "li.grid__item",
	cardWrapper: ".card-wrapper",
	heading: ".card__content .card__heading a",
	media: ".card__media img",
	price: ".card__content .price",
	colourways: ".card__colourways",
	badge: ".card__badge .badge",
	rfqBtn: ".rfq-collection-btn",
	rfqVariantInput: 'input[name="grfqId"]',
	rfqSelect: ".rfq-variant-selector",
	cartBubble: ".cart-count-bubble",
	// The theme cart drawer's dialog.
	cartDrawer: ".ARD-002_drawer",
};

const ENDPOINTS = {
	cart: "/cart.js",
	add: "/cart/add.js",
	product: (handle) => `/products/${handle}.js`,
};

const SHOP_ROOT = window.Shopify?.routes?.root || "/";

// Cards carry the product's whole image set; cap the carousel and fetch only the first two up front.
const MAX_SLIDES = 3;
const EAGER_SLIDES = 2;
const PILL_LIMIT = 3;
// Longer labels (e.g. "3mm x 340mm x 240mm") cannot sit side by side in a grid-width card, so they use the dropdown.
const PILL_MAX_LABEL = 14;
const IMAGE_WIDTH = 600;
const FLASH_RESET_MS = 2200;
const DRAG_THRESHOLD = 30;
// The mlveda currency app injects this option on some products; it is never a real choice.
const IGNORED_OPTION_NAMES = ["mlveda_country"];

const PLUS_SVG = `<svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true" focusable="false"><path d="M11.4969 19.7969C11.3617 19.6617 11.2941 19.4941 11.2941 19.2941V12.7059H4.70588C4.50588 12.7059 4.33827 12.6382 4.20306 12.5028C4.06769 12.3675 4 12.1998 4 11.9998C4 11.7996 4.06769 11.632 4.20306 11.4969C4.33827 11.3617 4.50588 11.2941 4.70588 11.2941H11.2941V4.70588C11.2941 4.50588 11.3618 4.33827 11.4972 4.20306C11.6325 4.06769 11.8002 4 12.0002 4C12.2004 4 12.368 4.06769 12.5031 4.20306C12.6383 4.33827 12.7059 4.50588 12.7059 4.70588V11.2941H19.2941C19.4941 11.2941 19.6617 11.3618 19.7969 11.4972C19.9323 11.6325 20 11.8002 20 12.0002C20 12.2004 19.9323 12.368 19.7969 12.5031C19.6617 12.6383 19.4941 12.7059 19.2941 12.7059H12.7059V19.2941C12.7059 19.4941 12.6382 19.6617 12.5028 19.7969C12.3675 19.9323 12.1998 20 11.9998 20C11.7996 20 11.632 19.9323 11.4969 19.7969Z" fill="currentColor"/></svg>`;

const TICK_SVG = `<svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true" focusable="false"><path d="M5 12.5 10 17.5 19 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const CHEVRON_SVG = `<svg viewBox="0 0 12 8" width="12" height="8" fill="none" aria-hidden="true" focusable="false"><path d="M10.72 1.16a.55.55 0 0 0-.78 0L5.94 5.17 1.94 1.16a.55.55 0 0 0-.78.78l4.39 4.39a.55.55 0 0 0 .78 0l4.39-4.39a.55.55 0 0 0 0-.78Z" fill="currentColor"/></svg>`;

const CHECK_SVG = `<svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false"><path d="M3 8.5 6.5 12 13 4.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const CLOSE_SVG = `<svg viewBox="0 0 14 14" width="14" height="14" fill="none" aria-hidden="true" focusable="false"><path d="M13.46.33.33 13.46M13.46 13.46.33.33" stroke="currentColor" stroke-width="0.94"/></svg>`;

const SHEET_HTML = `
	<div class="${testId}_sheet-backdrop"></div>
	<div class="${testId}_sheet" role="dialog" aria-modal="true" aria-label="Quick add">
		<button class="${testId}_sheet-close" type="button" aria-label="Close quick add">${CLOSE_SVG}</button>
		<div class="${testId}_sheet-product">
			<img class="${testId}_sheet-img" src="" alt="" width="56" height="56">
			<div class="${testId}_sheet-info">
				<p class="${testId}_sheet-title"></p>
				<div class="${testId}_sheet-price"></div>
			</div>
		</div>
		<div class="${testId}_sheet-options"></div>
	</div>
`;

// Per-card state, keyed by the card element so it goes when the grid re-renders.
const cardState = new WeakMap();
const productCache = new Map();

let sheetReturnFocus = null;
let sheetEl = null;
let sheetBackdropEl = null;
let activeCardEl = null;
let openCardEl = null;
let dragCardEl = null;
let dragStartX = 0;
let dragMoved = false;

const escapeHtml = (str) => String(str).replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));

/**
 * widgetConv returns symbols as HTML entities (e.g. "&pound;228"); labels are set as text, so decode first.
 */
const decodeEntities = (str) => {
	const el = document.createElement("textarea");
	el.innerHTML = String(str);
	return el.value;
};

/**
 * Formats cents in the shopper's currency through the Booster currency widget, which expects major units.
 */
const formatMoney = (cents) => (window.baCurr?.widgetConv ? decodeEntities(window.baCurr.widgetConv(cents / 100)) : `R ${(cents / 100).toLocaleString("en-ZA", { maximumFractionDigits: 0 })}`);

const sizedImage = (src, width) => (src.includes("?") ? `${src}&width=${width}` : `${src}?width=${width}`);

const getHandleFromUrl = (url) => (String(url).match(/\/products\/([^/?#]+)/) || [])[1] || null;

/**
 * Fetches product JSON once per handle.
 */
/**
 * Options the shopper actually chooses between: app-injected options and single-value options are dropped.
 */
const getRealOptions = (product) => product.options.map((option, index) => ({ name: typeof option === "string" ? option : option.name, values: typeof option === "string" ? [...new Set(product.variants.map(variant => variant.options[index]))] : option.values, index })).filter((option) => !IGNORED_OPTION_NAMES.includes(option.name.toLowerCase()) && option.values.length > 1);

const getVariantFor = (state) => state.product.variants.find((variant) => variant.available && state.realOptions.every((option, position) => variant.options[option.index] === state.selected[position])) || state.product.variants.find((variant) => state.realOptions.every((option, position) => variant.options[option.index] === state.selected[position]));

const getFallbackVariant = (product) => product.variants.find((variant) => variant.available) || product.variants[0];

/**
 * The variant that best matches the picks so far (even a partial pick) and has its own image.
 * Size variants share one photo, so size-only products return null and keep the product image.
 */
const getVariantWithImage = (state) => {
	const chosen = state.realOptions.map((option, position) => ({ index: option.index, value: state.selected[position] })).filter((entry) => entry.value);
	if (!chosen.length) return null;

	const matches = state.product.variants.filter((variant) => chosen.every((entry) => variant.options[entry.index] === entry.value));
	return matches.find((variant) => variant.available && variant.featured_image) || matches.find((variant) => variant.featured_image) || null;
};

/**
 * Points the card's first slide at the picked variant's photo (or back to the product image) and brings it to the front.
 * Swaps within the carousel rather than overlaying it, so hover, arrows and swipe keep working after a pick.
 */
const applyVariantImage = (cardEl, state) => {
	const variant = getVariantWithImage(state);
	const heroSrc = sizedImage(variant?.featured_image?.src || state.product.images[0] || "", IMAGE_WIDTH);

	const heroSlideEl = cardEl.querySelector(`.${testId}_slide`);
	if (!heroSlideEl) return;
	if (heroSlideEl.getAttribute("src") !== heroSrc) heroSlideEl.src = heroSrc;

	setSlide(cardEl, 0);

	// Keeps the mobile sheet's thumbnail in step while the sheet is open for this card.
	if (activeCardEl === cardEl && sheetEl.classList.contains(`${testId}_sheet--open`)) sheetEl.querySelector(`.${testId}_sheet-img`).src = heroSrc;
};

/**
 * Whether a value can still be bought given the shopper's other picks: some available variant must carry this value
 * and match every other chosen option. With one option this is a plain per-value stock check.
 */
const isValueAvailable = (state, position, value) =>
	state.product.variants.some((variant) => variant.available && variant.options[state.realOptions[position].index] === value && state.realOptions.every((option, other) => other === position || !state.selected[other] || variant.options[option.index] === state.selected[other]));

const buildPillsHTML = (state) =>
	`<div class="${testId}_pills" role="group" aria-label="${escapeHtml(state.realOptions[0].name)}">${state.realOptions[0].values
		.map((value) => `<button class="${testId}_pill" type="button" data-position="0" data-value="${escapeHtml(value)}"${isValueAvailable(state, 0, value) ? "" : " disabled"}>${escapeHtml(value)}</button>`)
		.join("")}</div>`;

const buildSelectHTML = (state) => {
	const showHeadings = state.realOptions.length > 1;

	const groups = state.realOptions
		.map(
			(option, position) =>
				`${showHeadings ? `<p class="${testId}_panel-heading">Select ${escapeHtml(option.name)}</p>` : ""}<div class="${testId}_panel-group" role="listbox" aria-label="${escapeHtml(option.name)}">${option.values
					.map((value) => `<button class="${testId}_panel-option" type="button" role="option" aria-selected="false" data-position="${position}" data-value="${escapeHtml(value)}">${escapeHtml(value)}<span class="${testId}_panel-flag"></span>${CHECK_SVG}</button>`)
					.join("")}</div>`
		)
		.join("");

	return `<div class="${testId}_select"><button class="${testId}_select-trigger" type="button" aria-expanded="false"><span class="${testId}_select-label">${escapeHtml(state.placeholder)}</span>${CHEVRON_SVG}</button><div class="${testId}_panel">${groups}</div></div>`;
};

const buildOptionsHTML = (state) => {
	const control = state.mode === "pills" ? buildPillsHTML(state) : state.realOptions.length ? buildSelectHTML(state) : "";

	return `${control}<button class="${testId}_cta" type="button">${escapeHtml(state.ctaLabel)}</button><p class="${testId}_error" hidden></p>`;
};

/**
 * Replaces the single image with up to three cross-fading slides, arrows (desktop) and dots (mobile).
 */
const renderMedia = (cardEl, product) => {
	const images = product.images.slice(0, MAX_SLIDES);
	if (images.length < 2) return;

	const mediaEl = cardEl.querySelector(`.${testId}_media`);
	const trackEl = mediaEl.querySelector(`.${testId}_track`);

	trackEl.innerHTML = images
		.map((src, index) => {
			const sized = escapeHtml(sizedImage(src, IMAGE_WIDTH));
			const source = index === 0 ? `src="${sized}"` : `data-src="${sized}"`;
			return `<img class="${testId}_slide${index === 0 ? ` ${testId}_slide--active` : ""}" ${source} alt="${escapeHtml(product.title)}" width="600" height="600" draggable="false">`;
		})
		.join("");

	mediaEl.insertAdjacentHTML(
		"beforeend",
		`<button class="${testId}_arrow ${testId}_arrow--prev" type="button" aria-label="Previous image">${CHEVRON_SVG}</button><button class="${testId}_arrow ${testId}_arrow--next" type="button" aria-label="Next image">${CHEVRON_SVG}</button><div class="${testId}_dots" aria-hidden="true">${images
			.map((_, index) => `<span class="${testId}_dot${index === 0 ? ` ${testId}_dot--active` : ""}"></span>`)
			.join("")}</div>`
	);

	cardState.get(cardEl).slideCount = images.length;

	// Desktop hover shows the second image and returns to the first on leave.
	mediaEl.addEventListener("mouseenter", () => {
		if (!isMobile.matches) setSlide(cardEl, 1);
	});

	mediaEl.addEventListener("mouseleave", () => {
		if (!isMobile.matches) setSlide(cardEl, 0);
	});
};

const loadSlide = (cardEl, index) => {
	const slideEl = cardEl.querySelectorAll(`.${testId}_slide`)[index];
	if (!slideEl?.dataset.src) return;

	slideEl.src = slideEl.dataset.src;
	delete slideEl.dataset.src;
};

/**
 * Shows a slide. The index wraps so the ends loop, and the slide after it is loaded ahead of the next swipe.
 */
const setSlide = (cardEl, index) => {
	const state = cardState.get(cardEl);
	if (state.slideCount < 2) return;

	state.slide = ((index % state.slideCount) + state.slideCount) % state.slideCount;

	loadSlide(cardEl, state.slide);
	loadSlide(cardEl, (state.slide + 1) % state.slideCount);

	cardEl.querySelectorAll(`.${testId}_slide`).forEach((slideEl, slideIndex) => slideEl.classList.toggle(`${testId}_slide--active`, slideIndex === state.slide));
	cardEl.querySelectorAll(`.${testId}_dot`).forEach((dotEl, dotIndex) => dotEl.classList.toggle(`${testId}_dot--active`, dotIndex === state.slide));
};

/**
 * Adds the quick-add icon to the price row. Sold-out products get none.
 */
const renderControl = (cardEl, state) => {
	if (state.mode === "none") return;

	cardEl
		.querySelector(`.${testId}_price-row`)
		.insertAdjacentHTML(
			"beforeend",
			`<button class="${testId}_add" type="button" aria-label="${state.mode === "quote" ? "Add to quote" : "Quick add to cart"}"><span class="${testId}_add-plus">${PLUS_SVG}</span><span class="${testId}_add-tick">${TICK_SVG}</span></button>`
		);
};

/**
 * Decides how the card adds: none (sold out), quote, direct (no options), pills (one short option) or select.
 */
const buildState = (cardEl, product) => {
	// Read off the product, not the quote app's button: the app only wires the first 20 grid items.
	const isQuote = product.variants.every((variant) => Number(variant.price) === 0);
	const realOptions = getRealOptions(product);

	let mode = "direct";
	if (!isQuote && !product.available) mode = "none";
	else if (isQuote) mode = "quote";
	else if (realOptions.length === 1 && realOptions[0].values.length <= PILL_LIMIT && realOptions[0].values.every((value) => value.length <= PILL_MAX_LABEL)) mode = "pills";
	else if (realOptions.length) mode = "select";

	return {
		product,
		itemEl: cardEl.closest(SELECTORS.item),
		mode,
		realOptions: mode === "direct" || mode === "none" ? [] : realOptions,
		placeholder: realOptions.length === 1 ? `Select ${realOptions[0].name}` : "Select Options",
		selected: [],
		slide: 0,
		slideCount: 1,
	};
};

const getCtaLabel = (state) => {
	if (state.mode === "quote") return "Add to Quote";

	const variant = state.realOptions.length && state.selected.filter(Boolean).length === state.realOptions.length ? getVariantFor(state) : null;

	return `Add to cart • ${formatMoney((variant || getFallbackVariant(state.product)).price)}`;
};

/**
 * Reflects the current picks and per-value stock in the pills or dropdown, its label, and the CTA price.
 */
const refreshOptionsUI = (hostEl, state) => {
	const chosen = state.realOptions.map((option, position) => state.selected[position]).filter(Boolean);

	hostEl.querySelectorAll(`.${testId}_pill`).forEach((pillEl) => { pillEl.disabled = !isValueAvailable(state, Number(pillEl.dataset.position), pillEl.dataset.value); pillEl.classList.toggle(`${testId}_pill--active`, state.selected[Number(pillEl.dataset.position)] === pillEl.dataset.value); pillEl.setAttribute("aria-pressed", String(state.selected[Number(pillEl.dataset.position)] === pillEl.dataset.value)); });

	hostEl.querySelectorAll(`.${testId}_panel-option`).forEach((optionEl) => {
		const position = Number(optionEl.dataset.position);
		const active = state.selected[position] === optionEl.dataset.value;
		optionEl.classList.toggle(`${testId}_panel-option--active`, active);
		optionEl.setAttribute("aria-selected", active ? "true" : "false");

		const available = isValueAvailable(state, position, optionEl.dataset.value);
		optionEl.disabled = !available;
		optionEl.classList.toggle(`${testId}_panel-option--soldout`, !available);
		optionEl.querySelector(`.${testId}_panel-flag`).textContent = available ? "" : "Sold out";
	});

	const labelEl = hostEl.querySelector(`.${testId}_select-label`);
	if (labelEl) labelEl.textContent = chosen.length === state.realOptions.length ? chosen.join(" • ") : state.placeholder;

	hostEl.querySelector(`.${testId}_cta`).textContent = getCtaLabel(state);
};

/**
 * Renders the option controls and CTA into the card or the sheet.
 * Pills open on the first available value so the CTA works straight away; the dropdown starts unset.
 */
const mountOptions = (hostEl, state) => {
	if (state.mode === "pills") state.selected = [state.realOptions[0].values.find((value) => isValueAvailable(state, 0, value)) || state.realOptions[0].values[0]];
	else state.selected = [];

	state.ctaLabel = getCtaLabel(state);
	hostEl.innerHTML = buildOptionsHTML(state);

	refreshOptionsUI(hostEl, state);
};

/**
 * Shows an error under the CTA. A direct add has no options open (hostEl is null), so the card's icon turns red instead.
 */
const showError = (cardEl, hostEl, message) => {
	if (!hostEl) {
        const error = cardEl.querySelector(`.${testId}_card-error`);
        error.textContent = message; error.hidden = false;
        return;
    }

	const errorEl = hostEl.querySelector(`.${testId}_error`);
	errorEl.textContent = message;
	errorEl.hidden = false;
};

const clearError = (hostEl) => {
	const errorEl = hostEl?.querySelector(`.${testId}_error`);
	if (!errorEl) return;

	errorEl.textContent = "";
	errorEl.hidden = true;
};

const closeSheet = (restoreFocus = true) => {
	if (!sheetEl.classList.contains(`${testId}_sheet--open`)) return;

	// Content stays so it does not vanish mid slide-down; the next open overwrites it.
	sheetEl.classList.remove(`${testId}_sheet--open`);
	sheetBackdropEl.classList.remove(`${testId}_sheet-backdrop--open`);
	document.documentElement.classList.remove(`${testId}_sheet-open`);
	activeCardEl = null;
	if (restoreFocus && sheetReturnFocus?.isConnected) sheetReturnFocus.focus();
};

const closeCardOptions = () => {
	if (!openCardEl) return;

	// The error line is cleared, or a failed add would leave the closed card taller.
	clearError(openCardEl.querySelector(`.${testId}_options`));
	openCardEl.classList.remove(`${testId}_card--open`);
	openCardEl.closest(SELECTORS.item).classList.remove(`${testId}_item--open`);
	openCardEl = null;
};

/**
 * Mobile: opens the bottom sheet with the product thumb, name, price and options.
 */
const openSheet = (cardEl, state) => {
	closeCardOptions();
	sheetReturnFocus = document.activeElement;
	activeCardEl = cardEl;

	sheetEl.querySelector(`.${testId}_sheet-img`).src = sizedImage(state.product.featured_image || state.product.images[0], 160);
	sheetEl.querySelector(`.${testId}_sheet-title`).textContent = state.product.title;
	sheetEl.querySelector(`.${testId}_sheet-price`).innerHTML = cardEl.querySelector(`.${testId}_price`).innerHTML;

	mountOptions(sheetEl.querySelector(`.${testId}_sheet-options`), state);

	sheetBackdropEl.classList.add(`${testId}_sheet-backdrop--open`);
	sheetEl.classList.add(`${testId}_sheet--open`);
	document.documentElement.classList.add(`${testId}_sheet-open`);

	// After the sheet is open, so its thumbnail follows any auto-selected value.
	applyVariantImage(cardEl, state);
	sheetEl.querySelector(`.${testId}_sheet-close`).focus();
};

/**
 * Desktop: swaps the card's title and price row for the options in place.
 */
const openCardOptions = (cardEl, state) => {
	closeCardOptions();
	openCardEl = cardEl;

	mountOptions(cardEl.querySelector(`.${testId}_options`), state);
	applyVariantImage(cardEl, state);
	cardEl.classList.add(`${testId}_card--open`);
	state.itemEl.classList.add(`${testId}_item--open`);
};

/**
 * Refreshes the header cart count. The theme's bubble carries its font size on the inner span; a bare number renders at 0.
 */
const updateBubble = () =>
	fetch(ENDPOINTS.cart, { headers: { Accept: "application/json" } })
		.then((res) => res.json())
		.then((cart) => {
			document.querySelectorAll(SELECTORS.cartBubble).forEach((bubbleEl) => {
				bubbleEl.innerHTML = cart.item_count > 0 ? `<span aria-hidden="true">(${cart.item_count})</span>` : "";
			});
		});

const flashAdded = (cardEl) => {
	cardEl.classList.add(`${testId}_card--added`);
	window.setTimeout(() => cardEl.classList.remove(`${testId}_card--added`), FLASH_RESET_MS);
};

/**
 * Hands the add to the cart drawer. The drawer opens by intercepting `/cart/add` form submits in the capture phase,
 * so a transient form makes quick add behave exactly like the product page. The drawer reads the FormData
 * synchronously on submit, so the form can be removed straight away.
 */
const addToCart = async (cardEl, state, hostEl) => {
    if (cardEl.classList.contains(`${testId}_card--busy`)) return;
    const variant = state.realOptions.length ? getVariantFor(state) : getFallbackVariant(state.product);
    if (!variant || state.realOptions.some((option, position) => !state.selected[position])) { showError(cardEl, hostEl, "Please choose an option first."); return; }
    if (!variant.available) { showError(cardEl, hostEl, "That option is sold out."); return; }
    clearError(hostEl);
    cardEl.querySelector(`.${testId}_card-error`).hidden = true;
    cardEl.classList.add(`${testId}_card--busy`);
    try {
        if (typeof window.ArdmoreCartDrawer?.add === "function") {
            closeSheet(false); closeCardOptions();
            const result = await window.ArdmoreCartDrawer.add({variantId: variant.id, handle: state.product.handle, quantity: 1, hostEl: cardEl});
            if (!result?.ok) { showError(cardEl, null, "This item could not be added. Please check the message in your bag."); return; }
        } else {
            const res = await fetch(`${SHOP_ROOT}cart/add.js`, {method:"POST", headers:{"Content-Type":"application/json",Accept:"application/json"}, body:JSON.stringify({items:[{id:variant.id,quantity:1}]})});
            const data = await res.json();
            if (!res.ok || data.status) throw new Error(data.description || "Could not add this item.");
            closeSheet(); closeCardOptions();
            window.location.assign(`${SHOP_ROOT}cart`);
        }
        flashAdded(cardEl);
    } catch (error) { showError(cardEl, hostEl, error.message || "Sorry, that could not be added. Please try again."); }
    finally { cardEl.classList.remove(`${testId}_card--busy`); }
};

/**
 * The quote app renders its button and variant input for the first 20 grid items only.
 * Later cards get the same hooks so the app's own addProduct path runs unchanged, popup included.
 */
const ensureQuoteHooks = (state) => state.itemEl.querySelector(SELECTORS.rfqBtn);

/**
 * Adds the resolved variant to the quote list through the RFQ app, or sends the shopper to the product page without it.
 */
const addToQuote = (cardEl, state, hostEl) => {
	const variant = state.realOptions.length ? getVariantFor(state) : getFallbackVariant(state.product);

	if (!variant) {
		showError(cardEl, hostEl, "Please choose an option first.");
		return;
	}

	const rfqBtnEl = ensureQuoteHooks(state);

	if (!rfqBtnEl) {
		window.location.href = `${state.product.url}?variant=${variant.id}`;
		return;
	}

	clearError(hostEl);

	// The app reads the variant off the hidden input scoped to this grid item.
	const input = state.itemEl.querySelector(SELECTORS.rfqVariantInput);
	if (!input) { window.location.href = `${state.product.url}?variant=${variant.id}`; return; }
	input.value = variant.id;

	const rfqSelectEl = state.itemEl.querySelector(SELECTORS.rfqSelect);
	if (rfqSelectEl) rfqSelectEl.value = String(variant.id);

	closeSheet();
	closeCardOptions();

	// The app's button carries an inline onclick, so a programmatic click runs its full pipeline.
	rfqBtnEl.click();
};

const handleAddClick = (cardEl) => {
	const state = cardState.get(cardEl);

	if (state.mode === "direct") {
		addToCart(cardEl, state, null);
		return;
	}

	if (isMobile.matches) openSheet(cardEl, state);
	else openCardOptions(cardEl, state);
};

/**
 * Resolves the options host (card or sheet), its card and state from a clicked element.
 */
const getContext = (target) => {
	const hostEl = target.closest(`.${testId}_sheet-options`) || target.closest(`.${testId}_options`);
	if (!hostEl) return null;

	const cardEl = hostEl.closest(`.${testId}_card`) || activeCardEl;
	if (!cardEl) return null;

	return { hostEl, cardEl, state: cardState.get(cardEl) };
};

/**
 * One set of delegated listeners for every card and the sheet, so they survive grid re-renders.
 */
const setupInteractions = () => {
	document.addEventListener(
		"pointerdown",
		(e) => {
			const mediaEl = e.target.closest(`.${testId}_media`);
			if (!mediaEl || e.target.closest(`.${testId}_arrow`)) return;

			dragCardEl = mediaEl.closest(`.${testId}_card`);
			dragStartX = e.clientX;
			dragMoved = false;
		},
		true
	);

	document.addEventListener(
		"pointerup",
		(e) => {
			if (!dragCardEl) return;

			const delta = e.clientX - dragStartX;
			const state = cardState.get(dragCardEl);

			if (state && Math.abs(delta) > DRAG_THRESHOLD) {
				dragMoved = true;
				setSlide(dragCardEl, state.slide + (delta < 0 ? 1 : -1));
			}

			dragCardEl = null;
		},
		true
	);

	document.addEventListener(
		"click",
		(e) => {
			// A swipe that ends on the media link must not follow through to the product page.
			if (dragMoved && e.target.closest(`.${testId}_media`)) {
				dragMoved = false;
				e.preventDefault();
				e.stopPropagation();
				return;
			}

			const arrowEl = e.target.closest(`.${testId}_arrow`);
			if (arrowEl) {
				e.preventDefault();
				const cardEl = arrowEl.closest(`.${testId}_card`);
				setSlide(cardEl, cardState.get(cardEl).slide + (arrowEl.classList.contains(`${testId}_arrow--next`) ? 1 : -1));
				return;
			}

			const addEl = e.target.closest(`.${testId}_add`);
			if (addEl) {
				e.preventDefault();
				handleAddClick(addEl.closest(`.${testId}_card`));
				return;
			}

			if (e.target.closest(`.${testId}_sheet-close`) || e.target === sheetBackdropEl) {
				closeSheet();
				return;
			}

			const triggerEl = e.target.closest(`.${testId}_select-trigger`);
			if (triggerEl) {
				const isOpen = triggerEl.nextElementSibling.classList.toggle(`${testId}_panel--open`);
				triggerEl.setAttribute("aria-expanded", isOpen ? "true" : "false");
				return;
			}

			const choiceEl = e.target.closest(`.${testId}_pill, .${testId}_panel-option`);
			if (choiceEl) {
				const context = getContext(choiceEl);
				if (!context) return;

				context.state.selected[Number(choiceEl.dataset.position)] = choiceEl.dataset.value;
				clearError(context.hostEl);
				refreshOptionsUI(context.hostEl, context.state);
				applyVariantImage(context.cardEl, context.state);

				// The dropdown closes once every option has a value.
				const panelEl = choiceEl.closest(`.${testId}_panel`);
				if (panelEl && context.state.selected.filter(Boolean).length === context.state.realOptions.length) {
					panelEl.classList.remove(`${testId}_panel--open`);
					panelEl.previousElementSibling.setAttribute("aria-expanded", "false");
				}
				return;
			}

			const ctaEl = e.target.closest(`.${testId}_cta`);
			if (ctaEl) {
				const context = getContext(ctaEl);
				if (!context) return;

				if (context.state.mode === "quote") addToQuote(context.cardEl, context.state, context.hostEl);
				else addToCart(context.cardEl, context.state, context.hostEl);
				return;
			}

			// Any click outside an open card collapses it back to the price row.
			if (openCardEl && !e.target.closest(`.${testId}_card--open`)) closeCardOptions();
		},
		true
	);

	document.addEventListener("keydown", (e) => {
        if (e.key === "Tab" && sheetEl.classList.contains(`${testId}_sheet--open`)) {
            const nodes = [...sheetEl.querySelectorAll('button:not(:disabled), [href], [tabindex="0"]')].filter(el => el.getClientRects().length);
            const first = nodes[0], last = nodes[nodes.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
		if (e.key !== "Escape") return;

		closeSheet();
		closeCardOptions();
	});

	// Crossing the breakpoint swaps sheet for in-card options, so whatever is open is the wrong surface.
	isMobile.addEventListener("change", () => {
		closeSheet();
		closeCardOptions();
	});
};

/**
 * Loads the product for a card and adds its carousel and quick-add control.
 */
const hydrateCard = (cardEl) => {
    if (cardState.has(cardEl)) return;
    try {
        const product = JSON.parse(cardEl.querySelector('[data-ardmore-product]').textContent);
        product.images = (product.images || []).map(image => typeof image === "string" ? image : image.src);
        product.featured_image = typeof product.featured_image === "string" ? product.featured_image : product.featured_image?.src;
        product.url = cardEl.querySelector(`.${testId}_title`).getAttribute("href");
        product.variants.forEach(variant => { variant.options ||= [variant.option1,variant.option2,variant.option3].filter(value => value != null); });
        const state = buildState(cardEl, product);
        cardState.set(cardEl, state);
        renderMedia(cardEl, product); renderControl(cardEl, state);
    } catch (error) { console.warn("Ardmore card could not initialise", error); }
};

/**
 * Adds the mobile bottom sheet to <body>, outside the grid.
 */
const createSheet = () => {
	const sheetRootEl = document.createElement("div");
	sheetRootEl.className = `${testId}_sheet-root`;
	sheetRootEl.innerHTML = SHEET_HTML;
	document.body.appendChild(sheetRootEl);

	sheetEl = sheetRootEl.querySelector(`.${testId}_sheet`);
	sheetBackdropEl = sheetRootEl.querySelector(`.${testId}_sheet-backdrop`);
};

/**
 * Initialises custom cards in any section. Idempotent hydration supports filtering, sliders,
 * recommendation responses and editor reloads. Native cards and media tiles lack data-ardmore-card and are skipped.
 */
const scanCards = () => {
    document.querySelectorAll('[data-ardmore-card]').forEach(hydrateCard);
};
const init = () => {
    createSheet(); setupInteractions(); scanCards();
    let queued = false;
    new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
            queued = false;
            if (activeCardEl && !activeCardEl.isConnected) closeSheet();
            if (openCardEl && !openCardEl.isConnected) openCardEl = null;
            scanCards();
        });
    }).observe(document.body, {childList:true,subtree:true});
    document.addEventListener('shopify:section:load', scanCards);
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true});
else init();

})();
